import React, { forwardRef, useImperativeHandle, useRef, memo, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Table, TableBody, TableCell, TableContainer, Chip,
         TableHead, TableRow, Paper, Radio, Box, Avatar, Button,  } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedIcon from '@mui/icons-material/Verified';

//each row local selected and knows its group controller
const PlanOptionRow = memo(
    forwardRef(({ logoUrl, option, name, registerToGroup, insurerID }, ref) => {
        const [selected, setSelected] = useState(false);

        //internal ref to expose imperative handle
        const localRef = useRef();

        useImperativeHandle(localRef, () => ({
            getSelected: () => (selected ? option.uid : null),
            getInsurerID: () => insurerID,
            select: () => setSelected(true),
            deselect: () => setSelected(false)
        }));

        //forward localRef as ref
        useImperativeHandle(ref, () => localRef.current);

        //register self to controller on mount
        React.useEffect(() => {
            registerToGroup(localRef);
        }, [registerToGroup]);

        return (
            <TableRow
                sx={{
                    backgroundColor: selected ? "rgba(0, 0, 0, 0.05)" : "inherit"
                }}
            >
                <TableCell>
                    <Avatar
                        src={logoUrl}
                        alt={option.name}
                        variant="square"
                        sx={{ width: 32, height: 32 }}
                    />
                </TableCell>
                <TableCell>{option.type}</TableCell>
                <TableCell>{option.name}</TableCell>
                <TableCell align="right">
          <span style={{ fontWeight: option.selected ? "bold" : "normal" }}>
            CHF {option.price.toFixed(2)}
          </span>
                </TableCell>
                <TableCell align="center">
                    <Radio
                        name={name}
                        checked={selected}
                        onChange={() => {
                            // select me and deselect others
                            registerToGroup.selectOnly(localRef);
                        }}
                    />
                </TableCell>
            </TableRow>
        );
    })
);

class RadioGroupController {
    constructor(insurerID, groupKey) {
        this.insurerID = insurerID;
        this.groupKey = groupKey;
        this.rows = [];
    }

    register(ref) {
        this.rows.push(ref);
    }

    selectOnly(refToSelect) {
        this.rows.forEach((ref) => {
            if (ref.current) {
                if (ref === refToSelect) {
                    ref.current.select();
                } else {
                    ref.current.deselect();
                }
            }
        });
    }

    getSelected() {
        for (const ref of this.rows) {
            if (ref.current) {
                const optionUID = ref.current.getSelected();
                if (optionUID) {
                    return {
                        insurerUID: ref.current.getInsurerID(),
                        optionUID: optionUID
                    };
                }
            }
        }
        return null;
    }
}

const Plans = forwardRef(({ plans, backBtnAction, nextBtnAction }, ref) => {
    //one group controller per user group key (mainUser, familyMember_0, etc)
    const groupControllers = useRef({});

    //register helper key only on user group (ignore insurerID to share across plans)
    const getController = (insurerID, groupKey) => {
        const key = groupKey; // key is only the group key for global selection per user group
        if (!groupControllers.current[key]) {
            groupControllers.current[key] = new RadioGroupController(insurerID, groupKey);
        }
        return groupControllers.current[key];
    };

    useImperativeHandle(ref, () => ({
        getSelectedPlanOptions: () => {
            const selected = {};
            Object.entries(groupControllers.current).forEach(([key, controller]) => {
                const selectedObj = controller.getSelected();
                if (selectedObj) {
                    selected[key] = selectedObj;
                }
            });
            return selected;
        }
    }));

    return (
        <Box>
            <br/>
            {plans.map((plan, idx) => (
                <Accordion key={plan.uid}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        {plan.logoUrl && (
                            <Avatar
                                src={plan.logoUrl}
                                alt={plan.name}
                                variant="square"
                                sx={{ width: 40, height: 40, mr: 2 }}
                            />
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                <span>{plan.name}</span>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <span>Gesamtpreis: CHF {plan.totalPrice.toFixed(2)}</span>
                            </Typography>
                        </Box>
                        {idx === 0 && (
                            <Chip
                                icon={<VerifiedIcon style={{ color: "white" }} />}
                                label="Günstigste"
                                size="small"
                                sx={{
                                    bgcolor: "success.main",
                                    color: "white",
                                    ml: 2,
                                    mr: 8,
                                    mt: 1.5,
                                    pointerEvents: "none"
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </AccordionSummary>

                    <AccordionDetails>
                        <Typography variant="h6" gutterBottom>
                            Main User
                        </Typography>
                        <TableContainer component={Paper} sx={{ mb: 4 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Logo</TableCell>
                                        <TableCell>Plan Type</TableCell>
                                        <TableCell>Option Name</TableCell>
                                        <TableCell align="right">Price (CHF)</TableCell>
                                        <TableCell align="center">Ausgewählt</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {plan.mainUser.plans.map((option, optionIdx) => {
                                        const rowRef = React.createRef();
                                        const group = getController(plan.insurerID, "mainUser");
                                        const registerToGroup = Object.assign(
                                            (ref) => group.register(ref),
                                            {
                                                selectOnly: (ref) => group.selectOnly(ref)
                                            }
                                        );
                                        return (
                                            <PlanOptionRow
                                                key={option.uid}
                                                logoUrl={plan.logoUrl}
                                                option={option}
                                                insurerID={plan.insurerID}
                                                name="mainUser"
                                                ref={rowRef}
                                                registerToGroup={registerToGroup}
                                            />
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {plan.familyMembers.map((member, memberIdx) => (
                            <Box key={memberIdx} sx={{ mb: 4 }}>
                                <Typography variant="h6" gutterBottom>
                                    Familienmitglied: <span>{member.firstName} {member.lastName}</span>
                                </Typography>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Logo</TableCell>
                                                <TableCell>Plantyp</TableCell>
                                                <TableCell>Optionsname</TableCell>
                                                <TableCell align="right">Preis (CHF)</TableCell>
                                                <TableCell align="center">Ausgewählt</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {member.plans.map((option, optionIdx) => {
                                                const rowRef = React.createRef();
                                                const group = getController(plan.insurerID, `familyMember_${memberIdx}`);
                                                const registerToGroup = Object.assign(
                                                    (ref) => group.register(ref),
                                                    {
                                                        selectOnly: (ref) => group.selectOnly(ref)
                                                    }
                                                );
                                                return (
                                                    <PlanOptionRow
                                                        key={option.uid}
                                                        logoUrl={plan.logoUrl}
                                                        option={option}
                                                        insurerID={plan.insurerID}
                                                        name={`familyMember_${memberIdx}`}
                                                        ref={rowRef}
                                                        registerToGroup={registerToGroup}
                                                    />
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ))}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: '100%',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Button color="inherit" variant="outlined" onClick={backBtnAction}>
                                Zurück
                            </Button>

                            <Box sx={{flex: 1}}/>
                            <Button variant="outlined"
                                    onClick={nextBtnAction}
                            >
                                Nächste
                            </Button>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
});

export default Plans;
